import { useState } from "react";
import { Bluetooth, Globe, Printer, Usb, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { encodeTestPage } from "@/lib/escpos";
import { UNSUPPORTED_COPY, connectBluetooth, connectUsb, loadPrinter, savePrinter, sendToPrinter, supports } from "@/lib/printers";
import { useAuth } from "@/context/AuthContext";

const MODES = [
  { k: "browser", label: "Browser print", icon: Globe, ok: () => true },
  { k: "usb", label: "USB printer", icon: Usb, ok: supports.usb },
  { k: "bluetooth", label: "Bluetooth printer", icon: Bluetooth, ok: supports.bluetooth },
];

export function PrinterCard() {
  const { session } = useAuth();
  const width = session.business.receipt_width;
  const [cfg, setCfg] = useState(loadPrinter);
  const [busy, setBusy] = useState(false);
  const update = (next) => {
    setCfg(next);
    savePrinter(next);
  };
  const device = cfg.mode === "usb" ? cfg.usb : cfg.mode === "bluetooth" ? cfg.bt : null;
  const anyUnsupported = !supports.usb() || !supports.bluetooth();

  const connect = async () => {
    setBusy(true);
    try {
      const info = cfg.mode === "usb" ? await connectUsb() : await connectBluetooth();
      update({ ...cfg, [cfg.mode === "usb" ? "usb" : "bt"]: info });
      toast.success(`Connected ${info.name}`);
    } catch (e) {
      if (e?.name !== "NotFoundError") toast.error("Printer not reachable", { description: e?.message });
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await sendToPrinter(cfg, encodeTestPage(width, device?.name));
      toast.success("Test page sent");
    } catch {
      toast.error("Printer not reachable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="printer-card">
      <div className="flex items-center gap-2"><Printer className="h-5 w-5 text-amber-600" /><h2 className="font-display text-lg font-bold text-slate-900">Receipt printer</h2></div>
      <p className="mt-1 text-sm text-slate-500">Saved on this device. Paper width is {width} ({width === "58mm" ? 32 : 48} characters per line).</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-3" role="radiogroup" data-testid="printer-mode-group">
        {MODES.map(({ k, label, icon: Icon, ok }) => {
          const enabled = ok();
          const on = cfg.mode === k;
          return (
            <button
              type="button"
              key={k}
              role="radio"
              aria-checked={on}
              disabled={!enabled}
              onClick={() => update({ ...cfg, mode: k })}
              data-testid={`printer-mode-${k}`}
              className={`flex h-14 items-center gap-2 rounded-xl border-2 px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${on ? "border-slate-900 bg-amber-50 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          );
        })}
      </div>
      {anyUnsupported && (
        <div className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600" data-testid="printer-unsupported-note">{UNSUPPORTED_COPY}</div>
      )}
      {cfg.mode !== "browser" && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button type="button" onClick={connect} disabled={busy} data-testid="printer-connect-button" className="flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white disabled:bg-slate-300">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Connect printer
          </button>
          {device && (
            <>
              <span className="rounded-lg bg-emerald-50 px-3 py-2 font-mono text-xs font-semibold text-emerald-800" data-testid="printer-device-name">{device.name}</span>
              <button type="button" onClick={test} disabled={busy} data-testid="printer-test-button" className="h-11 rounded-xl border-2 border-slate-900 px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50">Test print</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
